<template>
    <nav class="l2-nav" :class="{ 'l2-nav--scrolled': isCollapsed }" aria-label="Primary">
        <div class="l2-nav__container">
            <a class="l2-nav__logo" href="#top">
                <div class="l2-nav__logo-icon">
                    <LandingLogoIcon width="auto" :height="isCollapsed ? 36 : 85" />
                </div>
                <span class="l2-nav__logo-text">Mix n' Batch</span>
            </a>

            <div class="l2-nav__links">
                <a href="#features" class="l2-nav__link">Features</a>
                <a href="#pricing" class="l2-nav__link">Pricing</a>
                <a href="#faq" class="l2-nav__link">FAQ</a>
                <a href="#roadmap" class="l2-nav__link">Roadmap</a>
                <a href="#early-adopter" class="l2-nav__link" style="color: #7559d2; font-weight: bold" @click.prevent="$emit('early-access')">
                    Get Early Access
                </a>
            </div>

            <div class="l2-nav__actions">
                <v-btn class="l2-nav__theme-toggle" icon variant="text" :aria-label="themeToggleLabel" @click="$emit('toggle-theme')">
                    <v-icon size="20">{{ themeIcon }}</v-icon>
                </v-btn>
                <v-btn class="l2-nav__contact" variant="text" @click="$emit('contact')">Contact</v-btn>
            </div>
        </div>
    </nav>
</template>

<script lang="ts" setup>
    import LandingLogoIcon from '@/pages/Landing/components/LandingLogoIcon.vue';
    import type { LandingTheme } from '@/pages/Landing/composables/useLandingTheme';
    import { computed, onMounted, onUnmounted, ref, watch, withDefaults } from 'vue';

    const props = withDefaults(
        defineProps<{
            freezeScrolled?: boolean;
            theme?: LandingTheme;
        }>(),
        {
            freezeScrolled: false,
            theme: 'dark',
        },
    );

    defineEmits<{
        (e: 'contact'): void;
        (e: 'early-access'): void;
        (e: 'toggle-theme'): void;
    }>();

    const SMALL_SCREEN_BREAKPOINT = 650;

    // Keep deterministic for hydration; compute actual value after mount.
    const isSmallScreen = ref(false);
    const scrolled = ref(false);

    const isCollapsed = computed(() => scrolled.value || isSmallScreen.value);

    const themeToggleLabel = computed(() => (props.theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'));
    const themeIcon = computed(() => (props.theme === 'light' ? 'mdi-weather-night' : 'mdi-white-balance-sunny'));

    let mediaQuery: MediaQueryList | null = null;

    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
        isSmallScreen.value = e.matches;
    };

    onMounted(() => {
        mediaQuery = window.matchMedia(`(max-width: ${SMALL_SCREEN_BREAKPOINT}px)`);
        handleMediaChange(mediaQuery);
        mediaQuery.addEventListener('change', handleMediaChange);

        const setScrollListener = (enabled: boolean) => {
            window.removeEventListener('scroll', handleScroll);

            if (enabled) {
                window.addEventListener('scroll', handleScroll, { passive: true });
                handleScroll();
            } else {
                scrolled.value = false;
            }
        };

        watch(
            () => props.freezeScrolled,
            (frozen) => {
                setScrollListener(!frozen);
            },
            { immediate: true },
        );
    });

    const getEffectiveScrollY = (): number => {
        const bodyTop = document.body.style.top;
        const topValue = bodyTop.endsWith('px') ? Number(bodyTop.slice(0, -2)) : Number.NaN;

        if (!Number.isNaN(topValue) && topValue < 0) {
            return Math.abs(topValue);
        }

        return window.scrollY;
    };

    const handleScroll = () => {
        if (props.freezeScrolled) {
            return;
        }

        scrolled.value = getEffectiveScrollY() > 20;
    };

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll);
        mediaQuery?.removeEventListener('change', handleMediaChange);
    });
</script>

<style lang="scss" scoped>
    .l2-nav {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 100;
        padding: 16px 24px;
        transition: var(--l2-transition);

        // Force collapsed state on small screens
        @media (max-width: 650px) {
            background: var(--l2-nav-bg);
            backdrop-filter: blur(20px);
            border-bottom: 1px solid var(--l2-border-subtle);
            padding: 10px 16px;
        }

        &--scrolled {
            background: var(--l2-nav-bg);
            backdrop-filter: blur(20px);
            border-bottom: 1px solid var(--l2-border-subtle);
            padding: 10px 24px;

            @media (max-width: 650px) {
                padding: 10px 16px;
            }
        }

        &__container {
            max-width: 1280px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        &__logo {
            display: flex;
            align-items: center;
            gap: 10px;
            font-weight: 600;
            font-size: 1.25rem;
            color: var(--l2-text);
            text-decoration: none;
        }

        &__logo-icon {
            flex: 0 0 auto;
            display: flex;
            align-items: center;

            // Force small logo on mobile
            @media (max-width: 650px) {
                height: 36px;
                overflow: hidden;

                img {
                    height: 50px !important;
                    width: auto !important;
                }
            }
        }

        &__logo-text {
            white-space: nowrap;

            @media (max-width: 400px) {
                display: none;
            }
        }

        &__links {
            display: flex;
            gap: 32px;

            @media (max-width: 1150px) {
                display: none;
            }
        }

        &__contact {
            @media (max-width: 1150px) {
                display: none;
            }
        }

        &__link {
            color: var(--l2-text-muted);
            text-decoration: none;
            font-size: 0.9375rem;
            font-weight: 500;
            transition: color var(--l2-transition);

            &:hover {
                color: var(--l2-text);
            }

            &--purple {
                color: #a78bfa;

                &:hover {
                    color: #c4b5fd;
                }
            }
        }

        &__actions {
            display: flex;
            align-items: center;
            gap: 12px;

            @media (max-width: 650px) {
                gap: 4px;
            }
        }

        &__theme-toggle {
            transition: background-color var(--l2-transition);

            &:hover {
                background: var(--l2-accent-soft) !important;
            }
        }
    }
</style>
